import { takeLatest, put, call, select } from 'redux-saga/effects';
import { initialize, change } from 'redux-form';
import assign from 'lodash/assign';
import mapKeys from 'lodash/mapKeys';

import * as act from '../../actions';
import {
  LIBRARY_BRANDS_PAGE_REQUEST,
  LIBRARY_BRANDS_LIST_REQUEST,
  LIBRARY_BRANDS_ON_CHANGE_OUTLET_IDS,
  LIBRARY_BRANDS_ON_CLICK_ROW,
  LIBRARY_BRANDS_ON_CLICK_CANCEL,
  LIBRARY_BRANDS_SUBMIT_FORM_UPDATE,
  LIBRARY_BRANDS_ON_CLICK_CREATE_BRAND,
  LIBRARY_BRANDS_SUBMIT_FORM_CREATE,
  LIBRARY_BRANDS_SUBMIT_FORM_CREATE_ALL_OUTLETS,
  LIBRARY_BRANDS_DELETE_BRAND_REQUEST,
  LIBRARY_BRANDS_DELETE_BRAND,
  LIBRARY_BRANDS_CANCEL_DELETE,
} from '../../actions/library/brand/constants';
import * as actNotif from '../../actions/notifications';
import * as actBrand from '../../actions/library/brand';
import * as actOutletList from '../../actions/outletPages/list';
import { setSelectedOutletIds } from '../../actions/actionBar';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';

import * as apiOutlets from '../../api/outlets';
import * as apiBrand from '../../api/brands';
import * as apiLoyalty from '../../api/customers/loyalty';
import { getSubscriptionStatus } from '../../api/feature/getSubscriptionStatus';
import { getSelectedFirstOutlet } from '../../utils/userProperties';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapLibraryBrand from '../../clevertap/library/brand';

const BRAND_SUCCESSFULLY_CREATED_MESSAGE = 'Brand successfully created.';

let rewardBrands: any[] = [];
let pointGainBrands: any[] = [];
const featureNames = {
  loyalty: 'Loyalty',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  loyalty_pro: 'Loyalty Pro',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ingredient_inventory: 'Ingredient Inventory',
};

const getCloneNotification = (state) =>
  state.outletPages.list.showCloneOutletNotification.notification;

function* fillSearchFormFromOutletIds(outletIds) {
  let assignedOutletIds: any[] = [];
  if (outletIds.length > 1) {
    assignedOutletIds = [outletIds[0]];
  } else {
    assignedOutletIds = outletIds;
  }
  const data = { outletIds: assignedOutletIds };
  yield put(initialize('search', data));
}

function* fillFormBrandDetail(brand) {
  const formBrandDetail = {
    id: brand.id,
    name: brand.name,
  };
  yield put(initialize('brand', formBrandDetail));
}

function* openRightColumn() {
  yield put(actBrand.openRightColumn());
}

function* closeRightColumn() {
  yield put(actBrand.closeRightColumn());
}

function* getCloneOutletNotification() {
  const notification = yield select(getCloneNotification);
  return notification;
}

export function* getLoyalty(params) {
  yield put(act.showProgressBar());

  try {
    const response = yield call(apiLoyalty.getLoyalty, params);
    return response.data;
  } catch (error) {
    assertErrorHasMessage(error);
    // If we get an error we send Redux the appropiate action and return
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
    return false;
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* brandsPageRequestFlow() {
  yield put(actBrand.setListBrands([]));
  yield put(showPageLoadingSpinner(true));
  yield put(actBrand.setOutlets([]));
  yield put(act.showProgressBar());
  yield put(actBrand.setBrandDetail(null));
  yield put(actBrand.closeRightColumn());
  try {
    const response = yield call(apiOutlets.outlets);
    yield put(actBrand.setOutlets(response.data));
    const outletIds = getSelectedOutletIdFromLocalStorage();
    yield call(fillSearchFormFromOutletIds, outletIds);
    clevertapLibraryBrand.libraryBrandStart({
      currentOutlet: outletIds[0],
    });
    yield put(actBrand.brandsRequest({ page: 1 }));
    yield put(actOutletList.checkCloningOutletOnProgress());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* brandsRequestFlow(request) {
  const {
    payload: { params, previousBrands },
  } = request;
  try {
    yield put(act.showProgressBar());
    const loyaltyResponse = yield call(getLoyalty, null);
    const response = yield call(getSubscriptionStatus, {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      feature_names: Object.values(featureNames),
    });
    const subscriptionArray = response.data;
    const subscriptionDetail = assign(
      // @ts-expect-error ts-migrate(2448) FIXME: Block-scoped variable 'subscriptionDetail' used be... Remove this comment to see the full error message
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      subscriptionDetail,
      mapKeys(subscriptionArray, 'name')
    );
    rewardBrands = [];
    pointGainBrands = [];
    loyaltyResponse.forEach((program) => {
      if (
        program.status === 'ACTIVE' &&
        subscriptionDetail['Loyalty Pro'].is_active
      ) {
        program.rewards.forEach((reward) => {
          if (reward.reward_items) {
            reward.reward_items.forEach((rewardItem) => {
              if (rewardItem.type === 'BRAND') {
                rewardBrands.push(rewardItem.brand_name);
              }
            });
          }
        });
        program.earning_rules.forEach((earningRule) => {
          if (earningRule.type === 'ITEM') {
            earningRule.item_rules.forEach((rule) => {
              if (rule.type === 'BRAND') {
                pointGainBrands.push(rule.brand_name);
              }
            });
          }
        });
      }
    });
    const currentOutlet = yield select(getSelectedFirstOutlet);
    if (params.keyword) {
      const resp = yield call(apiBrand.getBrands, params, currentOutlet);
      const brands = resp.data.results;
      yield put(actBrand.setListBrands(brands));
      yield put(actBrand.setTotalPage(1));
      yield put(actBrand.setCurrentPage(1));
    } else {
      const resBrands = yield call(
        apiBrand.getBrands,
        {
          page: params.page,
        },
        currentOutlet
      );
      let brands;
      if (previousBrands) {
        brands = previousBrands.concat(resBrands.data.results);
      } else {
        brands = resBrands.data.results;
      }
      yield put(actBrand.setListBrands(brands));
      yield put(actBrand.setTotalPage(resBrands.data.total_pages));
      yield put(actBrand.setCurrentPage(params.page));
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* onChangeOutletIds(action) {
  yield put(actBrand.setListBrands([]));
  yield put(showPageLoadingSpinner(true));
  const {
    payload: { outletIds },
  } = action;
  const previousOutlet = yield select(getSelectedFirstOutlet);
  clevertapLibraryBrand.libraryBrandChangeOutlet({
    previousOutlet,
    currentOutlet: outletIds,
  });
  try {
    yield put(actBrand.setBrandDetail(null));
    yield call(closeRightColumn);
    yield call(fillFormBrandDetail, { id: null, name: null });
    yield put(act.showProgressBar());
    setSelectedOutletIdToLocalStorage(outletIds);
    yield put(actBrand.brandsRequest({ page: 1 }));
    yield put(setSelectedOutletIds(outletIds));
    yield put(change('search', 'outletIds', outletIds));
    yield put(actOutletList.checkCloningOutletOnProgress());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onClickRowFlow(request) {
  const {
    payload: { brand },
  } = request;
  try {
    yield put(act.showProgressBar());
    yield put(actBrand.hideUpdateModal());
    const currentOutlet = yield select(getSelectedFirstOutlet);
    const brandDetail = yield call(
      apiBrand.getBrandDetail,
      currentOutlet,
      brand.id
    );
    const brandDetailInfo = brandDetail.data;
    brandDetailInfo.is_reward_brand =
      rewardBrands.indexOf(brandDetailInfo.name) > -1;
    brandDetailInfo.is_point_gain_brand =
      pointGainBrands.indexOf(brandDetailInfo.name) > -1;
    yield put(actBrand.setBrandDetail(brandDetailInfo));
    yield call(fillFormBrandDetail, brandDetailInfo);
    yield call(openRightColumn);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onClickCancelFlow() {
  yield put(actBrand.setBrandDetail(null));
  yield call(fillFormBrandDetail, { id: null, name: null });
  yield call(closeRightColumn);
}

export function* updateBrandFlow(request) {
  const {
    payload: { id, data, previousBrands },
  } = request;
  const keyword = data.name;
  const type = 'edit';
  let oldBrandName = null;
  previousBrands.forEach((brand) => {
    if (brand.id === id) oldBrandName = brand.name;
  });
  clevertapLibraryBrand.libraryBrandUpdateOneOutlet({
    oldName: oldBrandName,
    newName: data.name,
  });
  try {
    yield put(act.showProgressBar());
    yield put(actBrand.isSubmittingForm(true));
    const currentOutlet = yield select(getSelectedFirstOutlet);
    yield call(apiBrand.brandRequestWithKeywordAndType, currentOutlet, {
      keyword,
      type,
    });
    const response = yield call(apiBrand.updateBrand, id, currentOutlet, data);
    const brandData = response.data;
    const brands: any[] = [];
    let brandExisted = false;
    previousBrands.forEach((brand) => {
      if (brand.id === id) {
        brands.push(brandData);
      } else if (keyword !== brand.name) {
        brands.push(brand);
      } else {
        brandExisted = true;
      }
    });
    if (brandExisted) {
      yield put(
        actNotif.error(
          'The name of the Brand must be unique',
          yield getCloneOutletNotification()
        )
      );
    } else {
      yield put(
        actNotif.success(
          'Brand was successfully updated.',
          yield getCloneOutletNotification()
        )
      );
      brandData.is_reward_brand = rewardBrands.indexOf(brandData.name) > -1;
      brandData.is_point_gain_brand =
        pointGainBrands.indexOf(brandData.name) > -1;
      yield put(actBrand.setListBrands(brands));
      yield put(actBrand.setBrandDetail(brandData));
      yield call(fillFormBrandDetail, brandData);
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(actBrand.isSubmittingForm(false));
  }
}

export function* onClickCreateBrandFlow() {
  yield put(actBrand.setBrandDetail(null));
  yield call(fillFormBrandDetail, { id: null, name: null });
  yield call(openRightColumn);
}

export function* createBrandFlow(request) {
  const {
    payload: { data, previousBrands },
  } = request;
  const keyword = data.name;
  const type = 'new';
  try {
    const currentOutlet = yield select(getSelectedFirstOutlet);
    yield put(act.showProgressBar());
    yield put(actBrand.isSubmittingForm(true));
    yield call(apiBrand.brandRequestWithKeywordAndType, currentOutlet, {
      keyword,
      type,
    });
    const response = yield call(apiBrand.createBrand, currentOutlet, data);
    const brandData = response.data;
    let brandExist = false;
    const brands = [brandData];
    previousBrands.forEach((brand) => {
      if (keyword !== brand.name) {
        brands.push(brand);
      } else {
        brandExist = true;
      }
    });
    if (brandExist) {
      yield put(actNotif.error('The name of the Brand must be unique'));
    } else {
      yield put(
        actNotif.success(
          BRAND_SUCCESSFULLY_CREATED_MESSAGE,
          yield getCloneOutletNotification()
        )
      );
      yield put(actBrand.setListBrands(brands));
      yield put(actBrand.setBrandDetail(brandData));
      yield call(fillFormBrandDetail, brandData);
    }
  } catch (error) {
    yield put(
      actNotif.error(
        error.message || 'Failed to create brand.',
        yield getCloneOutletNotification()
      )
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(actBrand.isSubmittingForm(false));
  }
}

export function* createBrandAllOutletsFlow(request) {
  const {
    payload: { data, previousBrands },
  } = request;
  try {
    const currentOutlet = yield select(getSelectedFirstOutlet);
    yield put(act.showProgressBar());
    yield put(actBrand.isSubmittingForm(true));
    const response = yield call(
      apiBrand.createBrandAllOutlets,
      currentOutlet,
      data
    );
    const brandData = response.data;
    const brands = [brandData];
    previousBrands.forEach((brand) => {
      brands.push(brand);
    });
    yield put(
      actNotif.success(
        BRAND_SUCCESSFULLY_CREATED_MESSAGE,
        yield getCloneOutletNotification()
      )
    );
    yield put(actBrand.setListBrands(brands));
    yield put(actBrand.setBrandDetail(brandData));
    yield call(fillFormBrandDetail, brandData);
  } catch (error) {
    yield put(
      actNotif.error(
        error.message || 'Failed to save brand to all outlets',
        yield getCloneOutletNotification()
      )
    );
  } finally {
    yield put(actBrand.isSubmittingForm(false));
    yield put(act.hideProgressBar());
  }
}

export function* deleteBrandRequest(request) {
  const {
    payload: { deletedBrand },
  } = request;
  yield put(actBrand.setDeletedBrand(deletedBrand));
  yield put(actBrand.showDeleteModal());
}

export function* deleteBrandFlow(request) {
  const {
    payload: { id, previousBrands },
  } = request;
  try {
    const currentOutlet = yield select(getSelectedFirstOutlet);
    yield put(act.showProgressBar());
    yield call(apiBrand.deleteBrand, currentOutlet, id);
    const brands: any[] = [];
    previousBrands.forEach((brand) => {
      if (brand.id !== id) {
        brands.push(brand);
      }
    });
    yield put(actBrand.setListBrands(brands));
    yield put(actBrand.setBrandDetail(null));
    yield put(actBrand.setDeletedBrand(null));
    yield put(actBrand.hideDeleteModal());
    yield call(closeRightColumn);
    yield put(
      actNotif.success(
        'Brand was successfully removed.',
        yield getCloneOutletNotification()
      )
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* cancelDeleteBrandFlow() {
  yield put(actBrand.setDeletedBrand(null));
  yield put(actBrand.hideDeleteModal());
}

export default function* saga() {
  yield takeLatest(LIBRARY_BRANDS_PAGE_REQUEST, brandsPageRequestFlow);
  yield takeLatest(LIBRARY_BRANDS_LIST_REQUEST, brandsRequestFlow);
  yield takeLatest(LIBRARY_BRANDS_ON_CHANGE_OUTLET_IDS, onChangeOutletIds);
  yield takeLatest(LIBRARY_BRANDS_ON_CLICK_ROW, onClickRowFlow);
  yield takeLatest(LIBRARY_BRANDS_ON_CLICK_CANCEL, onClickCancelFlow);
  yield takeLatest(LIBRARY_BRANDS_SUBMIT_FORM_UPDATE, updateBrandFlow);
  yield takeLatest(LIBRARY_BRANDS_SUBMIT_FORM_CREATE, createBrandFlow);
  yield takeLatest(
    LIBRARY_BRANDS_ON_CLICK_CREATE_BRAND,
    onClickCreateBrandFlow
  );
  yield takeLatest(
    LIBRARY_BRANDS_SUBMIT_FORM_CREATE_ALL_OUTLETS,
    createBrandAllOutletsFlow
  );
  yield takeLatest(LIBRARY_BRANDS_DELETE_BRAND_REQUEST, deleteBrandRequest);
  yield takeLatest(LIBRARY_BRANDS_DELETE_BRAND, deleteBrandFlow);
  yield takeLatest(LIBRARY_BRANDS_CANCEL_DELETE, cancelDeleteBrandFlow);
}
